import * as stylex from '@stylexjs/stylex'
import { type ComponentPropsWithoutRef, type ForwardedRef } from 'react'

const pulse = stylex.keyframes({
  '0%': {
    opacity: 1,
  },
  '50%': {
    opacity: 0.4,
  },
  '100%': {
    opacity: 1,
  },
})

const wave = stylex.keyframes({
  '0%': {
    transform: 'translateX(-100%)',
  },
  '50%': {
    /* +0.5s of delay between each loop */
    transform: 'translateX(100%)',
  },
  '100%': {
    transform: 'translateX(100%)',
  },
})

const skeletonVariants = stylex.create({
  text: {
    borderRadius: '4px / 6.7px',
    height: 'auto',
  },
  circular: {
    borderRadius: '50%',
  },
  rectangular: {},
  rounded: {
    borderRadius: 4,
  },
})

const animationVariants = stylex.create({
  pulse: {
    animationDelay: '0.5s',
    animationDuration: '2s',
    animationIterationCount: 'infinite',
    animationName: pulse,
    animationTimingFunction: 'ease-in-out',
  },
  wave: {
    /* Fix bug in Safari https://bugs.webkit.org/show_bug.cgi?id=68196 */
    WebkitMaskImage: '-webkit-radial-gradient(white, black)',
    overflow: 'hidden',
    position: 'relative',
  },
})

const dynamicStyles = stylex.create({
  height: (height: number | string) => ({
    height,
  }),
  width: (width: number | string) => ({
    width,
  }),
  fontSize: (fontSize: number | string) => ({
    fontSize,
  }),
})

const baseStyles = stylex.create({
  root: {
    backgroundColor: 'rgba(0, 0, 0, 0.11)',
    boxSizing: 'border-box',
    display: 'block',
    height: '1.2em',
  },
  waveAnimation: {
    animationDelay: '0.5s',
    animationDuration: '2s',
    animationIterationCount: 'infinite',
    animationName: wave,
    animationTimingFunction: 'linear',
    backgroundImage: `linear-gradient(
      90deg,
      transparent,
      rgba(0, 0, 0, 0.04),
      transparent
    )`,
    position: 'absolute',
    transform: 'translateX(-100%)' /* Avoid flash during server-side hydration */,
    bottom: 0,
    left: 0,
    right: 0,
    top: 0,
  },
})

export interface SkeletonProps extends ComponentPropsWithoutRef<'span'> {
  /** 根元素的 ref */
  rootRef?: ForwardedRef<HTMLSpanElement>
  /** 自訂根元素的 StyleX 樣式 */
  rootStyle?: stylex.StyleXStyles
  /**
   * 骨架屏的形狀變體
   * @default 'text'
   */
  variant?: 'text' | 'rectangular' | 'rounded' | 'circular'
  /**
   * 動畫類型，設為 false 可關閉動畫
   * @default 'pulse'
   */
  animation?: 'pulse' | 'wave' | false
  /** 骨架屏的高度，適用於非文字內容（如卡片） */
  height?: number | string
  /** 骨架屏的寬度，適用於行內元素 */
  width?: number | string
  /** 骨架屏的字體大小，適用於 text 變體 */
  fontSize?: number | string
}

/**
 * Skeleton 是骨架屏元件，用於在資料載入時顯示內容佔位。
 * 支援 pulse 與 wave 兩種動畫效果。
 */
export const Skeleton = (props: SkeletonProps) => {
  const {
    rootRef,
    rootStyle,
    animation = 'pulse',
    variant = 'text',
    height,
    width,
    fontSize,
    ...rest
  } = props

  return (
    <span
      ref={rootRef}
      {...stylex.props(
        baseStyles.root,
        skeletonVariants[variant],
        animation && animationVariants[animation],
        !!height && dynamicStyles.height(height),
        !!width && dynamicStyles.width(width),
        !!fontSize && dynamicStyles.fontSize(fontSize),
        rootStyle,
      )}
      {...rest}
    >
      <span {...stylex.props(animation === 'wave' && baseStyles.waveAnimation)}>&nbsp;</span>
    </span>
  )
}
